import React, { forwardRef, useId } from 'react';
import PropTypes from 'prop-types';
import { FIELD_SIZES } from '../../constants';
import { buildTextAreaClasses, FieldMessage } from './fieldHelpers';

const TextArea = forwardRef(({
  value,
  onChange,
  placeholder,
  disabled = false,
  error = false,
  rows = 4,
  size = 'md',
  className = '',
  helperText,
  errorMessage,
  id: idProp,
  ...props
}, ref) => {
  const generatedId = useId();
  const inputId = idProp || generatedId;
  const messageId = `${inputId}-message`;
  const hasMessage = Boolean(helperText || errorMessage);
  const hasError = error || Boolean(errorMessage);

  const textareaElement = (
    <textarea
      ref={ref}
      id={inputId}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      rows={rows}
      aria-invalid={hasError || undefined}
      aria-describedby={hasMessage ? messageId : undefined}
      className={buildTextAreaClasses({ size, error: hasError, className })}
      {...props}
    />
  );

  if (!hasMessage) return textareaElement;

  return (
    <div className="w-full">
      {textareaElement}
      <FieldMessage id={messageId} helperText={helperText} errorMessage={errorMessage} />
    </div>
  );
});

TextArea.displayName = 'TextArea';

TextArea.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  rows: PropTypes.number,
  size: PropTypes.oneOf(FIELD_SIZES),
  className: PropTypes.string,
  helperText: PropTypes.string,
  errorMessage: PropTypes.string,
  id: PropTypes.string
};

export default TextArea;
